import { IMAGE_TYPE, IMAGE_MODE } from '../../util/enums';
import { providerSupportsStop } from '../../util/provider';
import { unitFactor } from '../utils/autoTeardownUnits';

/**
 * A head-only cluster (no compute node groups) must STILL submit exactly one
 * node-group entry. init.js and several providers' Terraform index
 * `node.group[0]` in always-created head resources (e.g. Oracle's
 * `oci_core_instance_configuration`), so an empty `group: []` would blow up with
 * an out-of-range index. Emit a single placeholder that mirrors the head node's
 * instance/image so those references resolve to valid values; `count: 0` means
 * `updateTerraformNodes` instantiates ZERO compute modules, so no compute VM is
 * created. This is exactly the doc shape long-lived head-only clusters already
 * carry, so it is provably safe (no backend change).
 */
export function withHeadOnlyPlaceholder(groups, form) {
    if (groups && groups.length > 0) return groups;
    return [{
        count: 0,
        instance_id: form.serverInstanceId || '',
        image_type: form.serverImageType || IMAGE_TYPE.HEIDI_STACK,
        image_name: form.serverImageName || '',
        image_id: form.serverImageId || '',
        group_node_prefix: form.serverImageName || null,
    }];
}

/**
 * Shared flat → nested API payload mapping.
 * Providers extend this with their specific fields.
 */
export function baseToApiShape(form) {
    return {
        id: form.id,
        name: form.hostname || '',
        displayName: form.displayName || '',
        description: form.description || '',
        credential_id: form.credentialId || '',
        provider: form.provider,
        tags: form.tags || [],
        applications: form.applications || [],
        excludedGlobalScripts: form.excludedGlobalScripts || [],
        storageConfiguration: form.storageConfiguration || null,
        autoUpdate: form.autoUpdate || false,
        info: {
            'os-type': form.osType || '',
            bursting: { mode: form.burstingMode || 'off' },
            vncTokenAuthentication: form.vncTokenAuthentication || false,
            vncRemoteClient: form.vncRemoteClient || false,
            dcvEnabled: form.dcvEnabled || false,
            desktopEnabled: form.desktopEnabled || false,
            // Per-cluster Default Remote Desktop engine. Empty string means
            // "Auto" (let the launcher pick). An explicit 'vnc' or 'dcv'
            // value also tells the backend to skip its auto-add of Heidi
            // Desktop on Ubuntu 26 - lets users with custom-baked
            // VNC/xfce-only images (e.g. ParaTools E4S) keep their stack
            // as-is. See validate/cluster.js userOptedOutOfDesktop.
            defaultRemoteProtocol: form.defaultRemoteProtocol || null,
            imageMode: form.imageMode || IMAGE_MODE.UNIFIED,
            isMarketplace: form.isMarketplace || false,
            market_id: form.marketId || null,
            // Stored for debugging / traceability only. The actual AMI was already
            // resolved into marketplace_head_image_id / marketplace_compute_image_id
            // by the cluster form at create time, so these are informational and
            // not consumed by Terraform or the deploy path.
            market_arch: form.marketArch || null,
            market_os: form.marketOs || null,
            state: form.state,
            lastDeployTime: 0,
            lastDestroyTime: 0,
            // Auto teardown timer. `minutes` is canonical; `unit` is kept only
            // so the form can show back the unit the user picked. A provider
            // that cannot stop is pinned to 'destroy' here as well as in the
            // section component, so a stale form value can never submit a stop
            // the backend would silently turn into a destroy.
            autoTeardown: {
                enabled: form.autoTeardownEnabled || false,
                action: providerSupportsStop(form.provider)
                    ? (form.autoTeardownAction === 'destroy' ? 'destroy' : 'stop')
                    : 'destroy',
                minutes: Math.floor(Number(form.autoTeardownValue) * unitFactor(form.autoTeardownUnit)) || 0,
                unit: form.autoTeardownUnit || 'hours',
            },
            // Cluster-idle teardown. Same shape as the timer above, and
            // normalised again on the API side (libs/idleTeardown.normalise),
            // which is also where a window shorter than the floor is raised
            // rather than honoured.
            idleTeardown: {
                enabled: form.idleTeardownEnabled || false,
                action: providerSupportsStop(form.provider)
                    ? (form.idleTeardownAction === 'destroy' ? 'destroy' : 'stop')
                    : 'destroy',
                minutes: Math.floor(Number(form.idleTeardownValue) * unitFactor(form.idleTeardownUnit)) || 0,
                unit: form.idleTeardownUnit || 'hours',
                // Only meaningful alongside a stop - a destroyed cluster has
                // nothing left to start. Pinned here as well as in the API's
                // normaliser so a stale form value cannot submit a
                // contradiction.
                startOnLogin: (form.idleTeardownStartOnLogin || false)
                    && providerSupportsStop(form.provider)
                    && form.idleTeardownAction !== 'destroy',
            },
        },
        data: {
            manager: form.manager || 'none',
            provision: { script: form.provisionScript || '' },
            share: form.share || 'nfs',
            server: {
                instance_id: form.serverInstanceId || '',
                volume_size: form.serverVolumeSize || '',
                image_type: form.serverImageType || IMAGE_TYPE.HEIDI_STACK,
                image_name: form.serverImageName || '',
                image_id: form.serverImageId || '',
                isPrivate: form.serverIsPrivate || false,
                attachedVolumes: form.serverAttachedVolumes || [],
            },
            network: {
                region_id: form.regionId || '',
                zone_id: form.zoneId || '',
                enable_node_internet_access: form.enableNodeInternetAccess ?? true,
                enable_capacity_reservation: form.enableCapacityReservation ?? false,
            },
            node: {
                volume_size: form.nodeVolumeSize || '',
                idle_purge_s: form.idlePurgeS ?? '',
                group: withHeadOnlyPlaceholder(form.nodeGroups || [], form),
            },
            moab: {
                hostname: form.moabHostname || '',
                ip: form.moabIp || '',
            },
        },
    };
}

/**
 * Shared nested API cluster → flat form state mapping.
 * Providers extend this with their specific fields.
 */
export function baseFromApiShape(cluster) {
    return {
        id: cluster.id,
        displayName: cluster.displayName || '',
        hostname: cluster.name || '',
        description: cluster.description || '',
        credentialId: cluster.credential_id || '',
        provider: cluster.provider,
        tags: cluster.tags || [],
        applications: cluster.applications || [],
        excludedGlobalScripts: cluster.excludedGlobalScripts || [],
        storageConfiguration: cluster.storageConfiguration || null,

        osType: cluster.info?.['os-type'] || '',
        burstingMode: cluster.info?.bursting?.mode || 'off',
        vncTokenAuthentication: cluster.info?.vncTokenAuthentication || false,
        vncRemoteClient: cluster.info?.vncRemoteClient || false,
        dcvEnabled: cluster.info?.dcvEnabled || false,
        desktopEnabled: cluster.info?.desktopEnabled || false,
        defaultRemoteProtocol: cluster.info?.defaultRemoteProtocol || '',
        imageMode: cluster.info?.imageMode || IMAGE_MODE.UNIFIED,
        isMarketplace: cluster.info?.isMarketplace || false,
        marketId: cluster.info?.market_id || null,
        marketArch: cluster.info?.market_arch || null,
        marketOs: cluster.info?.market_os || null,

        manager: cluster.data?.manager || 'none',
        moabHostname: cluster.data?.moab?.hostname || '',
        moabIp: cluster.data?.moab?.ip || '',
        provisionScript: cluster.data?.provision?.script || '',
        share: cluster.data?.share || 'nfs',

        serverInstanceId: cluster.data?.server?.instance_id || '',
        serverVolumeSize: cluster.data?.server?.volume_size || '',
        serverImageType: cluster.data?.server?.image_type || IMAGE_TYPE.HEIDI_STACK,
        serverImageName: cluster.data?.server?.image_name || cluster.stack_name || '',
        serverImageId: cluster.data?.server?.image_id || '',
        serverIsPrivate: cluster.data?.server?.isPrivate || false,
        serverAttachedVolumes: cluster.data?.server?.attachedVolumes || [],

        regionId: cluster.data?.network?.region_id || '',
        zoneId: cluster.data?.network?.zone_id || '',
        enableNodeInternetAccess: cluster.data?.network?.enable_node_internet_access ?? true,
        enableCapacityReservation: cluster.data?.network?.enable_capacity_reservation ?? false,

        nodeVolumeSize: cluster.data?.node?.volume_size || '',
        nodeGroups: cluster.data?.node?.group || [],
        idlePurgeS: cluster.data?.node?.idle_purge_s ?? '',

        state: cluster.info?.state,
        autoUpdate: cluster.autoUpdate || false,

        autoTeardownEnabled: cluster.info?.autoTeardown?.enabled || false,
        autoTeardownAction: cluster.info?.autoTeardown?.action || 'stop',
        autoTeardownUnit: cluster.info?.autoTeardown?.unit || 'hours',
        // Stored in minutes; shown back in whichever unit was picked.
        autoTeardownValue: cluster.info?.autoTeardown?.minutes
            ? cluster.info.autoTeardown.minutes / unitFactor(cluster.info?.autoTeardown?.unit || 'hours')
            : '',

        idleTeardownEnabled: cluster.info?.idleTeardown?.enabled || false,
        idleTeardownAction: cluster.info?.idleTeardown?.action || 'stop',
        idleTeardownUnit: cluster.info?.idleTeardown?.unit || 'hours',
        idleTeardownValue: cluster.info?.idleTeardown?.minutes
            ? cluster.info.idleTeardown.minutes / unitFactor(cluster.info?.idleTeardown?.unit || 'hours')
            : '',
        idleTeardownStartOnLogin: cluster.info?.idleTeardown?.startOnLogin || false,
    };
}

/**
 * Shared defaults for new cluster form.
 * Providers extend with their specific defaults.
 */
export function baseInitNewForm(provider, { marketplaceConfig, clusterSettings, orgClusterSettings, marketId, marketArch, marketOs }) {
    // Whether DCV is OFFERED at all (the checkbox only renders when this is on)
    // and whether it is PRE-TICKED are two different questions - see
    // dcvEnabled below.
    const hprd = orgClusterSettings?.clusterSettings?.enableHighPerformanceRemoteDesktop
        || clusterSettings?.enableHighPerformanceRemoteDesktop;
    const orgRemoteProtocol = orgClusterSettings?.clusterSettings?.defaultRemoteProtocol
        || clusterSettings?.defaultRemoteProtocol;

    // marketplaceConfig.os_types may be a string (legacy flat entries) or an
    // array of OSes (variant-aware entries). Pick a sensible single-string
    // default for the osType form field either way.
    const initialOsType = Array.isArray(marketplaceConfig?.os_types)
        ? (marketOs || marketplaceConfig.os_types[0] || '')
        : (marketplaceConfig?.os_types || '');

    // Org / platform default for the heidi-pip preset opt-out. When ON,
    // pre-seed the new cluster with the heidi-pip-user-install-v1 presetId
    // in excludedGlobalScripts so the deploy step does NOT install
    // /etc/profile.d/heidi-pip.sh on the head. Only affects newly created
    // clusters; existing clusters keep their own excludedGlobalScripts.
    const disablePip = orgClusterSettings?.clusterSettings?.disableHeidiPipPreset
        || clusterSettings?.disableHeidiPipPreset
        || false;

    return {
        provider,
        isMarketplace: Boolean(marketplaceConfig),
        marketId: marketId || null,
        marketArch: marketArch || null,
        marketOs: marketOs || null,
        manager: marketplaceConfig?.resource_manager || 'none',
        osType: initialOsType,
        serverImageName: marketplaceConfig?.server_prefix || '',
        // Head-node-only by default. Most clusters need no compute nodes, so we
        // start with NO node group (the form shows just an "Add Node Group"
        // button). toApiShape re-injects the required count:0 placeholder via
        // withHeadOnlyPlaceholder when the user leaves it empty.
        nodeGroups: [],
        enableNodeInternetAccess: true,
        // Capacity reservation default: org setting wins, otherwise
        // platform setting, otherwise off. Mirrors the disablePip pattern
        // above so an org admin who flips the toggle in Settings >
        // Cluster Management gets new cluster forms pre-checked even if
        // the platform-level default is off.
        enableCapacityReservation: orgClusterSettings?.clusterSettings?.fastCapacityValidationDefault === true
            || clusterSettings?.fastCapacityValidationDefault === true,
        vncTokenAuthentication: clusterSettings?.vncTokenAuthentication ?? false,
        vncRemoteClient: clusterSettings?.vncRemoteClient ?? false,
        // Heidi Desktop is the default remote desktop on a new cluster, so DCV
        // starts UNTICKED even where it is available. Turning the "Enable DCV
        // Remote Desktop" org switch on used to pre-tick this box on every new
        // cluster, which made DCV the de-facto default engine for anyone who
        // had ever enabled it - ParaTools reported exactly that on AWS, where
        // DCV is free and the switch had been left on for years. The switch now
        // only decides whether DCV is OFFERED; an org that really wants it
        // pre-selected sets Default Remote Desktop Protocol to DCV in
        // Settings > Cluster Management.
        dcvEnabled: Boolean(hprd && orgRemoteProtocol === 'dcv'),
        // Heidi Desktop default OFF — the user opts in either by checking
        // "Heidi Desktop (WebRTC)" here (third-party AMIs already have
        // selkies installed) or by picking it from the Features dropdown
        // (Heidi-baked AMIs install it during the bake).
        desktopEnabled: false,
        excludedGlobalScripts: disablePip ? ['preset:heidi-pip-user-install-v1'] : [],
        // Auto teardown timer: OFF for every new cluster, on every provider.
        // A cluster must never acquire a lifetime nobody asked for.
        autoTeardownEnabled: false,
        autoTeardownAction: 'stop',
        autoTeardownValue: '',
        autoTeardownUnit: 'hours',
        // Idle teardown: OFF for every new cluster, on every provider, for the
        // same reason as the timer. A cluster must never acquire a way to
        // shut itself down that nobody asked for.
        idleTeardownEnabled: false,
        idleTeardownAction: 'stop',
        idleTeardownValue: '',
        idleTeardownUnit: 'hours',
        idleTeardownStartOnLogin: false,
    };
}

/**
 * Shared edit-mode initialization.
 * Resolves settings defaults and legacy data locations.
 * Providers extend with their specific overrides.
 */
export function baseInitEditForm(cluster, { clusterSettings, orgClusterSettings }) {
    const flat = baseFromApiShape(cluster);

    // Node groups with cleanup (strip per-group volume_size, ensure defaults).
    // Drop the head-only placeholder (count 0) so editing a head-node-only cluster
    // opens to the empty "Add Node Group" state instead of a confusing zero-count
    // card. Media-storage groups (AWS VolumeZ) are kept regardless of count.
    // toApiShape re-injects the placeholder on save, so this is display-only.
    flat.nodeGroups = (cluster.data?.node?.group || []).map((group) => {
        const { volume_size, ...rest } = group;
        return {
            ...rest,
            count: group.count !== undefined ? group.count : 1,
            instance_id: group.instance_id || '',
            image_name: group.image_name || cluster.stack_name || '',
        };
    }).filter((g) => Number(g.count) > 0 || g.nodeType === 'media');

    // Fallback paths for legacy data locations
    flat.osType = flat.osType || cluster.data?.info?.['os-type'] || '';
    flat.regionId = flat.regionId || cluster.server?.region || '';
    flat.zoneId = flat.zoneId || cluster.server?.zone || '';
    flat.serverImageName = flat.serverImageName || cluster.stack_name || '';

    // VNC/DCV: cluster value wins, then settings default
    flat.vncTokenAuthentication = cluster.info?.vncTokenAuthentication !== undefined
        ? cluster.info.vncTokenAuthentication
        : clusterSettings?.vncTokenAuthentication;
    flat.vncRemoteClient = cluster.info?.vncRemoteClient !== undefined
        ? cluster.info.vncRemoteClient
        : clusterSettings?.vncRemoteClient;
    if (cluster.info?.dcvEnabled !== undefined) {
        flat.dcvEnabled = cluster.info.dcvEnabled;
    } else {
        // Same rule as a new cluster (see baseInitNewForm): a cluster document
        // that predates the dcvEnabled field only opens with DCV ticked when
        // the org actually asked for DCV as its default protocol. Otherwise
        // reopening an old cluster would silently turn DCV on at the next save.
        const hprd = orgClusterSettings?.clusterSettings?.enableHighPerformanceRemoteDesktop
            || clusterSettings?.enableHighPerformanceRemoteDesktop;
        const orgRemoteProtocol = orgClusterSettings?.clusterSettings?.defaultRemoteProtocol
            || clusterSettings?.defaultRemoteProtocol;
        flat.dcvEnabled = Boolean(hprd && orgRemoteProtocol === 'dcv');
    }
    flat.desktopEnabled = cluster.info?.desktopEnabled ?? false;

    // Collections (check both possible API locations).
    // Root level (cluster.excludedGlobalScripts) is the canonical write target
    // for both baseToApiShape and the API-side update preserve logic; the
    // legacy data-level location was an early-2024 path that some old
    // documents still carry as an EMPTY ARRAY. Reading data first with `||`
    // would pick `[]` over a populated root-level field (`[] || X` returns
    // `[]` in JS because empty arrays are truthy), wiping every exclusion
    // the user previously set the next time they reopen the form. Prefer
    // root level whenever it has entries; fall back to the legacy field only
    // when root is empty/missing.
    const rootExcluded = cluster.excludedGlobalScripts;
    const dataExcluded = cluster.data?.excludedGlobalScripts;
    flat.excludedGlobalScripts = (rootExcluded?.length > 0)
        ? rootExcluded
        : ((dataExcluded?.length > 0) ? dataExcluded : []);
    flat.applications = (cluster.applications?.length > 0) ? cluster.applications : [];
    flat.tags = (cluster.tags?.length > 0) ? cluster.tags : [];

    // Network defaults
    flat.enableNodeInternetAccess = cluster.data?.network?.enable_node_internet_access ?? true;
    flat.enableCapacityReservation = cluster.data?.network?.enable_capacity_reservation ?? false;

    return flat;
}
